import React, { CSSProperties, memo } from 'react';
import { useTranslation } from 'react-i18next';
import Tippy from '@tippyjs/react/headless';
import styled from 'styled-components';
import { PiCheckBold, PiCaretDownBold } from 'react-icons/pi';

export const VNFlag: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    width={size}
    height={Math.round((size * 2) / 3)}
    viewBox="0 0 30 20"
    style={{
      borderRadius: 2,
      display: 'inline-block',
      verticalAlign: 'middle',
      flexShrink: 0,
      boxShadow: '0 0 1px rgba(0, 0, 0, 0.25)',
    }}
  >
    <rect width="30" height="20" fill="#da251d" />
    <polygon
      points="15,4 16.54,8.76 21.56,8.76 17.51,11.71 19.05,16.47 15,13.53 10.95,16.47 12.49,11.71 8.44,8.76 13.46,8.76"
      fill="#ffff00"
    />
  </svg>
);

export const UKFlag: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    width={size}
    height={Math.round((size * 2) / 3)}
    viewBox="0 0 60 40"
    style={{
      borderRadius: 2,
      display: 'inline-block',
      verticalAlign: 'middle',
      flexShrink: 0,
      boxShadow: '0 0 1px rgba(0, 0, 0, 0.25)',
    }}
  >
    <clipPath id="uk-flag-clip-switcher">
      <rect width="60" height="40" rx="2" />
    </clipPath>
    <g clipPath="url(#uk-flag-clip-switcher)">
      <rect width="60" height="40" fill="#012169" />
      <path d="M0,0 L60,40 M60,0 L0,40" stroke="#ffffff" strokeWidth="8" />
      <path d="M0,0 L60,40 M60,0 L0,40" stroke="#C8102E" strokeWidth="3" />
      <path d="M30,0 v40 M0,20 h60" stroke="#ffffff" strokeWidth="12" />
      <path d="M30,0 v40 M0,20 h60" stroke="#C8102E" strokeWidth="7" />
    </g>
  </svg>
);

export interface LanguageItem {
  code: string;
  name: string;
  short: string;
  Flag: React.FC<{ size?: number }>;
}

export const LANGUAGES: LanguageItem[] = [
  { code: 'vn', name: 'Tiếng Việt', short: 'VN', Flag: VNFlag },
  { code: 'en', name: 'English', short: 'EN', Flag: UKFlag },
];

export interface SwitcherLngPropsType {
  style?: CSSProperties;
  theme?: 'light' | 'dark';
  showLabel?: boolean;
}

const isLanguageActive = (langCode: string, currentLang?: string) => {
  if (!currentLang) return langCode === 'vn';
  if (langCode === 'vn') return currentLang === 'vn' || currentLang.startsWith('vi');
  if (langCode === 'en') return currentLang === 'en' || currentLang.startsWith('en');
  return langCode === currentLang;
};

const SwitcherLng: React.FC<SwitcherLngPropsType> = ({
  style,
  theme = 'light',
  showLabel = true,
}) => {
  const { i18n } = useTranslation();

  const currentLanguage =
    LANGUAGES.find((item) => isLanguageActive(item.code, i18n.language)) || LANGUAGES[0];
  const CurrentFlag = currentLanguage.Flag;

  return (
    <Tippy
      trigger="click"
      interactive
      offset={[0, 8]}
      placement="bottom-end"
      delay={0}
      render={(attrs) => (
        <UI_DropdownWrapper {...attrs}>
          {LANGUAGES.map((item) => {
            const active = isLanguageActive(item.code, i18n.language);
            const FlagComponent = item.Flag;
            return (
              <UI_DropdownItem
                key={item.code}
                $active={active}
                onClick={() => i18n.changeLanguage(item.code)}
              >
                <FlagComponent size={20} />
                <span className="name">{item.name}</span>
                {active && <PiCheckBold className="check" />}
              </UI_DropdownItem>
            );
          })}
        </UI_DropdownWrapper>
      )}
    >
      <UI_TriggerWrapper $theme={theme} style={style} title="Chọn ngôn ngữ / Select language">
        <CurrentFlag size={18} />
        {showLabel && <UI_LangLabel>{currentLanguage.short}</UI_LangLabel>}
        <PiCaretDownBold className="chevron" />
      </UI_TriggerWrapper>
    </Tippy>
  );
};

export default memo(SwitcherLng);

const UI_TriggerWrapper = styled.div<{ $theme?: 'light' | 'dark' }>`
  display: inline-flex;
  cursor: pointer;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 6px;
  user-select: none;
  transition: all 0.2s ease;
  font-size: 13px;
  font-weight: 500;
  color: ${({ $theme }) => ($theme === 'dark' ? '#ffffff' : '#475569')};

  &:hover {
    background: ${({ $theme }) =>
      $theme === 'dark' ? 'rgba(255, 255, 255, 0.14)' : 'rgba(59, 130, 246, 0.08)'};
    color: ${({ $theme }) => ($theme === 'dark' ? '#ffffff' : 'var(--color-primary)')};
  }

  .chevron {
    font-size: 11px;
    opacity: 0.7;
    transition: transform 0.2s ease;
  }
`;

const UI_LangLabel = styled.span`
  letter-spacing: 0.02em;
  font-weight: 600;
`;

const UI_DropdownWrapper = styled.div`
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #f1f5f9;
  padding: 6px 0;
  min-width: 150px;
  z-index: 2500;
  overflow: hidden;
`;

const UI_DropdownItem = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? '600' : '450')};
  color: ${({ $active }) => ($active ? '#2563eb' : '#334155')};
  background-color: ${({ $active }) => ($active ? 'rgba(59, 130, 246, 0.08)' : 'transparent')};

  &:hover {
    background-color: rgba(59, 130, 246, 0.12);
    color: #1d4ed8;
  }

  .name {
    flex: 1;
  }

  .check {
    font-size: 14px;
    color: #2563eb;
  }
`;
